import React, { useState } from "react";
import { Form, Input, Button, Card, Typography, App, Divider } from "antd";
import { MailOutlined, PhoneOutlined, EnvironmentOutlined, SendOutlined, UserOutlined, MessageOutlined } from "@ant-design/icons";
import "./Contact.css";

const { Title, Text } = Typography;
const { TextArea } = Input;

function Contact() {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const { message } = App.useApp();

    const onFinish = async (values) => {
        if (!navigator.onLine) {
            message.error("You are offline. Check your internet connection.");
            return;
        }

        try {
            setLoading(true);
            message.destroy();

            // Simulate API call - replace with actual API endpoint
            await new Promise(resolve => setTimeout(resolve, 1500));

            // Uncomment when you have backend ready:
            // await API.post("/contact", values);

            message.success(`Thanks ${values.name}, we'll get back to you within 24 hours!`);
            form.resetFields();
        } catch (error) {
            message.error("Failed to send message. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const onFinishFailed = (errorInfo) => {
        message.destroy();

        const errorFields = errorInfo.errorFields;

        const allEmpty = errorFields.every((field) =>
            field.errors[0]?.includes("required")
        );

        if (allEmpty) {
            message.error("Please fill in all required fields");
            return;
        }

        const firstError = errorFields[0];
        const fieldName = firstError.name[0];

        let msg = "Please enter valid details";

        if (fieldName === "name") {
            msg = "Please enter your name";
        } else if (fieldName === "email") {
            msg = "Please enter a valid email";
        } else if (fieldName === "message") {
            msg = "Please enter your message";
        }

        message.error(msg);
    };

    return (
        <div className="contact-page">
            <div className="contact-container">
                <Card className="contact-card" variant="borderless">
                    <div className="contact-header">
                        <div className="brand-section">
                            <div className="brand-logo">D</div>
                            <Title level={3} className="brand-name">Draptor</Title>
                        </div>
                        <Title level={4} className="contact-title">
                            Get in touch
                        </Title>
                        <Text className="contact-subtitle">
                            We'd love to hear from you. Send us a message and we'll respond within 24 hours.
                        </Text>
                    </div>

                    <Form
                        layout="vertical"
                        form={form}
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                        validateTrigger="onSubmit"
                        requiredMark={false}
                    >
                        <Form.Item
                            label="Full Name"
                            name="name"
                            className="form-item"
                            rules={[{ required: true, message: "Please enter your name" }]}
                        >
                            <Input
                                placeholder="Enter your full name"
                                className="input-field"
                                prefix={<UserOutlined className="input-prefix" />}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Email"
                            name="email"
                            className="form-item"
                            rules={[
                                { required: true, message: "Please enter your email" },
                                { type: "email", message: "Enter a valid email" }
                            ]}
                        >
                            <Input
                                placeholder="Enter your email"
                                className="input-field"
                                prefix={<MailOutlined className="input-prefix" />}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Phone (Optional)"
                            name="phone"
                            className="form-item"
                        >
                            <Input
                                placeholder="Enter your phone number"
                                className="input-field"
                                prefix={<PhoneOutlined className="input-prefix" />}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Message"
                            name="message"
                            className="form-item"
                            rules={[{ required: true, message: "Please enter your message" }]}
                        >
                            <TextArea
                                placeholder="Tell us about your project or inquiry..."
                                className="textarea-field"
                                rows={4}
                            />
                        </Form.Item>

                        <Form.Item className="form-item">
                            <Button
                                type="primary"
                                htmlType="submit"
                                block
                                className="submit-btn"
                                loading={loading}
                                icon={<SendOutlined />}
                            >
                                Send Message
                            </Button>
                        </Form.Item>

                        <Divider className="custom-divider">Contact Info</Divider>

                        <div className="contact-info-grid">
                            <div className="info-item">
                                <MailOutlined className="info-icon" />
                                <div>
                                    <Text className="info-label">Email</Text>
                                    <Text className="info-value">support@draptor.com</Text>
                                </div>
                            </div>
                            <div className="info-item">
                                <PhoneOutlined className="info-icon" />
                                <div>
                                    <Text className="info-label">Phone</Text>
                                    <Text className="info-value">+1 (555) 000-DRAP</Text>
                                </div>
                            </div>
                            <div className="info-item">
                                <EnvironmentOutlined className="info-icon" />
                                <div>
                                    <Text className="info-label">Office</Text>
                                    <Text className="info-value">123 Tech Plaza, Silicon Valley</Text>
                                </div>
                            </div>
                        </div>
                    </Form>
                </Card>
            </div>
        </div>
    );
}

export default Contact;